import React, { useEffect } from 'react';
import { PRESET_AVATARS, CONNECTION_STATES } from '../../constants';
import './AvatarStage.css';

// Persona colour palettes — match reference design
const PERSONA_COLORS = {
  'assistant-1': { a: '#6C6EFF', b: '#9B6CFF', c: '#4FD8C4', swatch: 'linear-gradient(135deg,#6C6EFF,#9B6CFF)' },
  'assistant-2': { a: '#FF9E5E', b: '#FF5E7E', c: '#FFD166', swatch: 'linear-gradient(135deg,#FF9E5E,#FF5E7E)' },
  'assistant-3': { a: '#4FD8C4', b: '#6C6EFF', c: '#9B6CFF', swatch: 'linear-gradient(135deg,#4FD8C4,#6C6EFF)' },
};

export function AvatarStage({
  idleVideoRef,
  talkVideoRef,
  connectionStatus,
  isTalking,
  avatarUrl,
  onSelectAvatar,
  avatarPreview,
}) {
  const isLive = connectionStatus === CONNECTION_STATES.CONNECTED || connectionStatus === CONNECTION_STATES.TALKING;

  const currentPersona = PRESET_AVATARS.find((p) => p.url === avatarUrl) || null;
  const colors = currentPersona ? (PERSONA_COLORS[currentPersona.id] || PERSONA_COLORS['assistant-1']) : PERSONA_COLORS['assistant-1'];

  // Apply orb CSS vars to root whenever persona changes
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--orb-a', colors.a);
    root.style.setProperty('--orb-b', colors.b);
    root.style.setProperty('--orb-c', colors.c);
  }, [colors]);

  const stageClass = [
    'stage-viewport',
    isLive ? 'is-live' : '',
    isTalking ? 'is-talking' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="avatar-stage">
      {/* Main video/orb stage */}
      <div className={stageClass}>

        {/* Status overlay — top-left */}
        <div className="viewport-overlay-tag">
          <span className={`live-dot ${isLive ? 'active' : ''}`} />
          <span>{isTalking ? 'Speaking' : isLive ? 'Live' : 'Offline'}</span>
        </div>

        {/* Persona name — top-right */}
        <div className="persona-label-tag">
          {currentPersona ? currentPersona.name : 'O7 Assistant'}
          <span>{currentPersona ? currentPersona.role : 'AI Support'}</span>
        </div>

        {/* Animated conic orb (standby / when D-ID not streaming) */}
        <div className="orb-wrap">
          <div className="orb-glow" />
          <div className="orb-ring" />
          
          <div className="orb-content-mask">
            <div className="orb" />
            
            {(avatarPreview || (currentPersona && currentPersona.preview)) && (
              <img src={avatarPreview || (currentPersona && currentPersona.preview)} alt="Persona" className="avatar-preview-img" />
            )}

            {/* D-ID WebRTC talk stream */}
            <video
              ref={talkVideoRef}
              autoPlay
              playsInline
              className="video-layer talk-layer"
            />

            {/* Keyword video (infra etc.) */}
            <video
              ref={idleVideoRef}
              playsInline
              className="video-layer idle-layer"
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </div>

      {/* Persona selector strip */}
      <div className="persona-bar">
        <span className="persona-bar-label">Persona</span>
        {PRESET_AVATARS.map((preset) => {
          const pc = PERSONA_COLORS[preset.id] || PERSONA_COLORS['assistant-1'];
          const isSelected = currentPersona && currentPersona.id === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              className={`persona-pill ${isSelected ? 'is-active' : ''}`}
              onClick={() => onSelectAvatar(preset.url, preset.preview, preset.voiceId)}
            >
              <img
                src={preset.preview}
                alt={preset.name}
                className="persona-swatch"
              />
              <span>{preset.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
